export type OwnerProfile = {
  name: string
  email: string
  title: string
  timezone: string
  updatedAt: string | null
}

type ProfileResponse = {
  profile?: unknown
  message?: unknown
}

let ownerProfileCache: OwnerProfile | null = null
let ownerProfileRequest: Promise<OwnerProfile> | null = null

function isOwnerProfile(
  value: unknown,
): value is OwnerProfile {
  if (!value || typeof value !== 'object') {
    return false
  }

  const profile =
    value as Record<string, unknown>

  return (
    typeof profile.name === 'string' &&
    typeof profile.email === 'string' &&
    typeof profile.title === 'string' &&
    typeof profile.timezone === 'string' &&
    (
      profile.updatedAt === null ||
      typeof profile.updatedAt === 'string'
    )
  )
}

async function readProfileResponse(
  response: Response,
): Promise<OwnerProfile> {
  const contentType =
    response.headers.get('content-type') ?? ''

  if (!contentType.includes('application/json')) {
    throw new Error(
      'The secure profile endpoint is unavailable.',
    )
  }

  const body =
    await response.json() as ProfileResponse

  if (
    !response.ok ||
    !isOwnerProfile(body.profile)
  ) {
    throw new Error(
      typeof body.message === 'string'
        ? body.message
        : 'Profile data could not be loaded.',
    )
  }

  return body.profile
}

async function fetchOwnerProfile(): Promise<OwnerProfile> {
  const response = await fetch('/api/profile', {
    credentials: 'include',
    headers: {
      accept: 'application/json',
    },
  })

  return readProfileResponse(response)
}

export async function loadOwnerProfile(
  force = false,
): Promise<OwnerProfile> {
  if (!force && ownerProfileCache) {
    return ownerProfileCache
  }

  if (!force && ownerProfileRequest) {
    return ownerProfileRequest
  }

  ownerProfileRequest = fetchOwnerProfile()

  try {
    const profile =
      await ownerProfileRequest

    ownerProfileCache = profile

    return profile
  } finally {
    ownerProfileRequest = null
  }
}

export async function saveOwnerProfile(
  profile: Pick<
    OwnerProfile,
    'name' | 'email' | 'title' | 'timezone'
  >,
): Promise<OwnerProfile> {
  const response = await fetch('/api/profile', {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify(profile),
  })

  const saved =
    await readProfileResponse(response)

  ownerProfileCache = saved

  return saved
}

export async function uploadOwnerProfilePhoto(
  file: File,
): Promise<string> {
  const form = new FormData()
  form.append('photo', file)

  const response = await fetch(
    '/api/profile-photo',
    {
      method: 'PUT',
      credentials: 'include',
      body: form,
      headers: {
        accept: 'application/json',
      },
    },
  )

  const contentType =
    response.headers.get('content-type') ?? ''

  if (!contentType.includes('application/json')) {
    throw new Error(
      'The profile photo endpoint is unavailable.',
    )
  }

  const body =
    await response.json() as {
      uploaded?: unknown
      updatedAt?: unknown
      message?: unknown
    }

  if (
    !response.ok ||
    body.uploaded !== true
  ) {
    throw new Error(
      typeof body.message === 'string'
        ? body.message
        : 'Profile photo could not be saved.',
    )
  }

  return typeof body.updatedAt === 'string'
    ? body.updatedAt
    : new Date().toISOString()
}
